import {
  Button,
  Modal,
  Textarea,
  Group,
  Stack,
  Anchor,
  Select,
  Space,
  Text,
} from '@mantine/core';
import { useState } from 'react';
import { primaryColor } from '../../../../constants/theme';
import { useMutation } from '@tanstack/react-query';
import { useApp } from '../../../../context/AppContext';
import {
  BookingResponseModel,
  DisputeInputModel,
} from '../../../../types/BookingModels';
import { ErrorModal } from '../error/ErrorModal';
import { BookingPreview } from './BookingPreview';
import { raiseDispute } from '../../../../services/BookingService';

interface DisputeButtonBookingButtonProps {
  refreshBookings: () => void;
  profileId: string;
  groupedBookings: BookingResponseModel[];
}

const disputeOptions = [
  {
    label: 'They did not have the correct expertise',
    value: '1',
  },
  {
    label: 'Professional did not show up',
    value: '2',
  },
  {
    label: 'Recieved false information',
    value: '3',
  },
];

export const DisputeButtonBookingButton: React.FC<
  DisputeButtonBookingButtonProps
> = ({ refreshBookings, profileId, groupedBookings }) => {
  const booking = groupedBookings[0];
  const finalBooking = groupedBookings[groupedBookings.length - 1];
  const { token } = useApp();
  const [opened, setOpened] = useState(false);
  const [disputeButtonReason, setDisputeButtonReason] = useState('');
  const isProfessional = profileId === booking?.professionalId;
  const [explanation, setExplanation] = useState<string | undefined>(undefined);
  const {
    mutate: disputeButtonBookingMutation,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      const input: DisputeInputModel = {
        bookingId: booking.id,
        explanation: explanation ?? '',
        disputeReason: Number(disputeButtonReason),
        professionalId: booking.professionalId,
      };
      await raiseDispute(input, token || '');
    },
    onSuccess: () => {
      refreshBookings();
      setOpened(false);
      setDisputeButtonReason('');
    },
    onError: (error) => {
      console.error('Error disputing booking:', error);
    },
  });

  if (booking.disputeRaised) {
    return (
      <Text ta="end" c="dimmed" fz="sm">
        Dispute Raised
      </Text>
    );
  }

  const endTime = new Date(finalBooking.endIso);
  const disputeDeadline = new Date(endTime.getTime() + 5 * 24 * 60 * 60 * 1000); // 5 days after end time
  const now = new Date();

  const canRaiseDispute =
    profileId !== booking.professionalId &&
    endTime < now &&
    now <= disputeDeadline &&
    booking.accepted &&
    !booking.isCancelled &&
    !booking.disputeRaised;

  if (!canRaiseDispute) return null;

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="There was an error raising a dispute. Please try again."
        dismissAction={() => reset()}
        retryAction={() => disputeButtonBookingMutation()}
        isLoading={isPending}
      />
      <Anchor
        onClick={() => setOpened(true)}
        ta="end"
        c="dimmed"
        fz="sm"
        td="underline"
      >
        Not happy with your call?
      </Anchor>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
      >
        <Stack gap={10}>
          <Stack justify="center" align="center" gap={10}>
            <BookingPreview
              isProfessional={isProfessional}
              title="Dispute Booking?"
              groupedBookings={groupedBookings}
              bottomText="We will review your dispute and get back to you shortly."
            />
          </Stack>
          <Select
            withAsterisk
            label="Select a reason"
            placeholder="The issue type"
            data={disputeOptions}
            onChange={(value) => setDisputeButtonReason(value || '')}
          />
          <Textarea
            mt={15}
            label="Dispute Reason"
            placeholder="An explanation of the issue"
            rows={5}
            onChange={(e) => setExplanation(e.target.value)}
            withAsterisk
          />
          <Space h={5} />
          <Group gap={10} justify="center">
            <Button
              variant="outline"
              w="48%"
              color={primaryColor}
              onClick={() => setOpened(false)}
            >
              Cancel
            </Button>
            <Button
              w="48%"
              color={primaryColor}
              onClick={() => disputeButtonBookingMutation()}
              loading={isPending}
              disabled={!disputeButtonReason || !explanation}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
